import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Modal, Empty, StatusBadge } from "../components/ui";
import { Plus, X, Copy } from "lucide-react";

const MODULES = [
  { value: "legal_permits", label: "Requisitos Legales" },
  { value: "wells", label: "Pozos CONAGUA" },
  { value: "racs", label: "RACs" },
  { value: "procedures", label: "Procedimientos" },
  { value: "studies", label: "Estudios Normativos" },
  { value: "providers", label: "Proveedores" },
  { value: "report:conagua", label: "Reporte CONAGUA" },
  { value: "report:global", label: "Reporte Global" },
];

export default function ExternalTokens() {
  const [tokens, setTokens] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ module: "", label: "", hours: 24, uses: 1 });
  const [createdToken, setCreatedToken] = useState(null);

  async function load() {
    const { data } = await api.get("/external/tokens");
    setTokens(data);
  }
  useEffect(() => { load(); }, []);

  async function create() {
    const { data } = await api.post("/external/tokens", form);
    setCreatedToken(data);
    setForm({ module: "", label: "", hours: 24, uses: 1 });
    load();
  }

  async function revoke(id) {
    if (!window.confirm("¿Revocar este token?")) return;
    await api.post(`/external/tokens/${id}/revoke`);
    load();
  }

  function tokenUrl(t) {
    return `${window.location.origin}/external/${t}`;
  }

  return (
    <div data-testid="external-tokens">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="card-h1" style={{ margin: 0 }}>Accesos Externos (Tokens de un solo uso)</h2>
          <div className="text-xs text-dim mt-2">Comparte un módulo o reporte específico sin dar acceso completo a la plataforma.</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNew(true)} data-testid="new-token-btn">
          <Plus size={14} /> Nuevo token
        </button>
      </div>

      <div className="table-wrap">
        <table className="table">
          <thead><tr><th>Etiqueta</th><th>Módulo</th><th>Correo externo</th><th>Vencimiento</th><th>Usos</th><th>Status</th><th style={{ textAlign: "right" }}>Acciones</th></tr></thead>
          <tbody>
            {!tokens.length ? <tr><td colSpan={7}><Empty title="Sin tokens activos" sub="Genera el primero." /></td></tr> : tokens.map((t) => (
              <tr key={t.id}>
                <td><b>{t.label}</b></td>
                <td className="text-xs">{t.module}</td>
                <td className="text-xs">{t.external_email || "-"}</td>
                <td className="text-xs">{new Date(t.expires_at).toLocaleString("es-MX")}</td>
                <td>{t.uses_remaining}</td>
                <td><StatusBadge value={t.revoked ? "Suspendido" : (t.uses_remaining > 0 ? "Activo" : "Agotado")} /></td>
                <td style={{ textAlign: "right" }}>
                  <div className="flex justify-end gap-1">
                    <button className="btn btn-sm" onClick={() => { navigator.clipboard.writeText(tokenUrl(t.token)); alert("URL copiada al portapapeles"); }} title="Copiar URL"><Copy size={12} /></button>
                    {!t.revoked && <button className="btn btn-sm btn-danger" onClick={() => revoke(t.id)} title="Revocar"><X size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showNew} onClose={() => { setShowNew(false); setCreatedToken(null); }} title="Generar token de acceso externo" testId="new-token-modal">
        {createdToken ? (
          <div className="grid gap-3">
            <div className="card" style={{ background: "rgba(29,158,117,0.1)", border: "1px solid var(--accent)" }}>
              <div className="font-semibold mb-2">✅ Token generado</div>
              <div className="text-xs text-dim mb-3">Comparte esta URL con el usuario externo. Expira en {form.hours || 24} horas.</div>
              <div className="p-2 rounded" style={{ background: "var(--bg-3)", wordBreak: "break-all", fontFamily: "monospace", fontSize: 12 }}>
                {tokenUrl(createdToken.token)}
              </div>
              <button className="btn btn-primary btn-sm mt-3" onClick={() => navigator.clipboard.writeText(tokenUrl(createdToken.token))}>
                <Copy size={14} /> Copiar URL
              </button>
            </div>
            <div className="flex justify-end"><button className="btn" onClick={() => { setShowNew(false); setCreatedToken(null); }}>Cerrar</button></div>
          </div>
        ) : (
          <div className="grid gap-3">
            <div><label className="label">Etiqueta descriptiva *</label><input className="input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Auditor externo - Acceso a permisos" data-testid="token-label" /></div>
            <div><label className="label">Módulo o reporte *</label>
              <select className="select" value={form.module} onChange={(e) => setForm({ ...form, module: e.target.value })} required data-testid="token-module">
                <option value="">-- Seleccione --</option>
                {MODULES.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div><label className="label">Correo del usuario externo</label><input type="email" className="input" value={form.external_email} onChange={(e) => setForm({ ...form, external_email: e.target.value })} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="label">Horas de vigencia</label><input type="number" className="input" value={form.hours} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} /></div>
              <div><label className="label">Usos máximos</label><input type="number" className="input" value={form.uses} onChange={(e) => setForm({ ...form, uses: Number(e.target.value) })} /></div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <button className="btn" onClick={() => setShowNew(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={create} disabled={!form.module || !form.label} data-testid="create-token-btn">Generar token</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
